import React, { useState } from 'react';
import { Box, Paper, Stack, styled, Switch, Typography } from '@mui/material';
import { HexColorInput, HexColorPicker } from 'react-colorful';
import { useTranslation } from 'react-i18next';

const DEFAULT_COLOR = '#aabbcc';
interface ColorPickerProps {
    options?: {
        transparency: boolean;
    };
    value?: string;
    onChange?: (color: string) => void;
}

const ColorBox = styled(Paper)(({ theme }) => ({
    width: 32,
    height: 32,
    cursor: 'pointer',
    margin: 2,
    borderRadius: 4,
    border: '1px solid rgba(0,0,0,0.1)',
    transition: 'transform 0.2s',
    '&:hover': {
        transform: 'scale(1.1)',
        boxShadow: theme.shadows[3],
    },
}));

export const ColorPicker: React.FC<ColorPickerProps> = ({ value, options, onChange }) => {
    const { t } = useTranslation();
    const [color, setColor] = useState(value ?? DEFAULT_COLOR);

    const handleColorSelect = (newColor: string) => {
        if (onChange) {
            onChange(newColor);
        }
    };

    const handleTransparencyToggle = () => {
        const newColor = color === 'transparent' ? DEFAULT_COLOR : 'transparent';
        setColor(newColor);
        handleColorSelect(newColor);
    };

    return (
        <Box>
            <Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        maxWidth: 200,
                        gap: 1,
                    }}
                >
                    {value !== 'transparent' && (
                        <>
                            <HexColorPicker
                                color={color}
                                onChange={(color) => {
                                    setColor(color);
                                    handleColorSelect(color);
                                }}
                            />
                            <Stack direction={'row'} spacing={0.5}>
                                <ColorBox aria-label='color-box' sx={{ backgroundColor: color }} />
                                <HexColorInput
                                    color={color}
                                    onChange={(color) => {
                                        setColor(color);
                                        handleColorSelect(color);
                                    }}
                                    style={{ width: '165px' }}
                                />
                            </Stack>
                        </>
                    )}
                    {options?.transparency && (
                        <Box sx={{ alignItems: 'flex-start', display: 'flex', gap: 1 }}>
                            <Switch size='small' checked={color === 'transparent' || false} onChange={handleTransparencyToggle} />
                            <Typography variant='body1'>{t('common.transparency')}</Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};
